import { FC, memo } from 'react'

import { Text } from '@cash-me/react-components'
import NextImage from 'next/image'

import { Col, Grid } from 'components/grid'
import { Polygon } from 'components/polygon'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'

import * as S from './styles'

const IMAGES_PATH = `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/integrity-program`

/* As duas fotos têm proporções diferentes, então largura e altura trocam junto com o arquivo. */
const HERO_IMAGE = { src: `${IMAGES_PATH}/hero-portal.webp`, width: 2238, height: 1156 }
const HERO_IMAGE_MOBILE = { src: `${IMAGES_PATH}/hero-portal-mobile.webp`, width: 1600, height: 1067 }

const pillars = ['Compliance', 'ESG', 'Privacidade', 'Gestão de Risco']

const Hero: FC = memo(() => {
  const { isTablet } = useWindowSize()

  return (
    <S.Wrapper>
      <S.Hero aria-labelledby="integrity-hero-title">
        <S.LineWrapper aria-hidden>
          <S.MobileLine />
          <NextImage src={`${IMAGES_PATH}/hero-line.svg`} alt="" width={502} height={317} />

          <S.PolygonWrapper>
            <Polygon type="TriangleRound" width={44} height={56} draw="fill" color={theme.colors['cashme-magenta']} />
          </S.PolygonWrapper>
        </S.LineWrapper>

        <Grid col={{ phone: 1, tablet: 12 }}>
          <Col col={{ tablet: 10, desktop: 6 }} start={{ tablet: 2, desktop: 2 }}>
            <S.Infos>
              <Text
                id="integrity-hero-title"
                as="h1"
                size="heading2"
                weight="medium"
                color="var(--colors-neutral-text-contrast-default)"
              >
                Boas-vindas ao
                <br />
                <Text as="span" size="heading2" weight="medium" color="var(--colors-cyan-text-contrast)">
                  Portal de <br />
                  Integridade!
                </Text>
              </Text>

              <S.Paragraphs>
                <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                  Na CashMe integridade é prioridade!
                </Text>
                <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                  Aqui você encontra as ações do nosso Programa de Integridade, que nos impulsionam a colaborar com um
                  ambiente mais justo e transparente.
                </Text>
                <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                  Nossa cultura focada em ética e responsabilidade é reforçada pelos seguintes pilares:
                </Text>
              </S.Paragraphs>
            </S.Infos>
          </Col>
        </Grid>

        <S.ImageWrapper>
          <NextImage
            {...(isTablet ? HERO_IMAGE : HERO_IMAGE_MOBILE)}
            alt="Equipe da CashMe trabalhando em suas estações no escritório"
            sizes="(min-width: 1240px) 59vw, 100vw"
            quality={82}
            priority
          />
        </S.ImageWrapper>
      </S.Hero>

      <S.Cards>
        <Grid col={{ phone: 1, tablet: 12 }}>
          <Col col={{ tablet: 10, desktop: 10 }} start={{ tablet: 2, desktop: 2 }}>
            <S.CardsRow>
              <S.PillarsCard>
                <Text size="heading5" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                  Comprometimento da{' '}
                  <Text as="span" size="heading5" weight="medium" color="var(--colors-cyan-background-pressed)">
                    Alta Administração
                  </Text>
                </Text>

                <S.PillarsList>
                  {pillars.map(pillar => (
                    <S.Pillar key={pillar}>
                      <Text size="heading3" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                        {pillar}
                      </Text>
                    </S.Pillar>
                  ))}
                </S.PillarsList>

                <Text size="heading5" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                  Comprometimento dos{' '}
                  <Text as="span" size="heading5" weight="medium" color="var(--colors-cyan-background-pressed)">
                    Colaboradores
                  </Text>
                </Text>
              </S.PillarsCard>

              <S.QuoteCard>
                <S.QuoteMark aria-hidden>
                  <NextImage src={`${IMAGES_PATH}/quote-open.svg`} alt="" width={54} height={30} />
                </S.QuoteMark>

                <Text size="body1" color="var(--colors-neutral-text-default)">
                  <Text as="span" size="body1" color="var(--colors-pink-text-contrast)">
                    Vamos juntos
                  </Text>{' '}
                  transformar o comum em extraordinário. Mas não de qualquer jeito nem a qualquer custo.
                </Text>

                <S.QuoteMark $align="end" aria-hidden>
                  <NextImage src={`${IMAGES_PATH}/quote-close.svg`} alt="" width={54} height={30} />
                </S.QuoteMark>
              </S.QuoteCard>
            </S.CardsRow>
          </Col>
        </Grid>
      </S.Cards>
    </S.Wrapper>
  )
})

Hero.displayName = 'Hero'

export default Hero
